// import { loadStripe } from '@stripe/stripe-js';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import axios from 'utilities/axios';
import queryClient from 'utilities/queryClient';
import { getDateOnly } from 'utilities/time';

const API = {
  PAYMENT: {
    GET_STRIPE_PUBLISH_KEY: 'payments/subscriptions/key',
    PAYMENT_METHOD: 'payment-method',
    DELETE_PAYMENT_METHOD: (id) => `payment-method/${id}`,
    DELETE_PAYMENT_METHOD_ACH: (id) => `payment-method/ach/${id}`,
    PAYMENT_INTENT: 'payments/intents',
    CREATE_ACH: 'payment-method/ach',
    VERIFY_ACH: 'payment-method/ach/verify',
    CHARGE_ACH: 'payments/charge',
    TRANSACTIONS: 'payments/history',
    REQUEST_FUNDS: 'users/request-funds',
    TRACKING_PAYMENT_METHOD: 'payment-method/tracking',
    TRACKING_ABANDONED_CARTS: 'tracking-events/abandoned-cart',
    TRACKING_AUDIT_PAYMENT_METHOD: 'tracking-events/audit-payment-method',
    TRACKING_OPENING_CREATIVE_SHOP: 'tracking-events/opening-creative-shop',
    DEFAULT_PAYMENT_METHOD: 'payment-method/default',
  },
};

export const useGetStripePublishKey = () => {
  return useQuery('stripePublishKey', () =>
    axios.get(API.PAYMENT.GET_STRIPE_PUBLISH_KEY).then((res) => res.data)
  );
};

export const useCreatePaymentMethod = () => {
  return useMutation(
    (values) => axios.post(API.PAYMENT.PAYMENT_METHOD, values),
    {
      onError: () => {},
      onSuccess: () => {
        queryClient.invalidateQueries('payment-methods');
      },
    }
  );
};

export const useGetListPaymentMethod = () => {
  return useQuery('payment-methods', () =>
    axios.get(API.PAYMENT.PAYMENT_METHOD).then((res) => res.data)
  );
};

export const useTrackingPaymentMethod = () => {
  return useMutation(
    (values) => axios.post(API.PAYMENT.TRACKING_PAYMENT_METHOD, values),
    {
      onError: () => {},
    }
  );
};

export const useTrackingAuditPaymentMethod = () => {
  return useMutation(
    (values) => axios.post(API.PAYMENT.TRACKING_AUDIT_PAYMENT_METHOD, values),
    {
      onError: () => {},
    }
  );
};

export const useTrackingOpeningCreativeShop = () => {
  return useMutation(
    () => axios.post(API.PAYMENT.TRACKING_OPENING_CREATIVE_SHOP),
    {
      onError: () => {},
    }
  );
};

export const useDeletePaymentMethod = () => {
  return useMutation(
    ({ cardId, type }) =>
      axios.delete(
        type === 'AchAccount'
          ? API.PAYMENT.DELETE_PAYMENT_METHOD_ACH(cardId)
          : API.PAYMENT.DELETE_PAYMENT_METHOD(cardId)
      ),
    {
      onSuccess: (_, { onCallback }) => {
        onCallback();
        queryClient.invalidateQueries('payment-methods');
      },
    }
  );
};

export const useGetPaymentIntent = () => {
  return useMutation(
    (values) =>
      axios.post(API.PAYMENT.PAYMENT_INTENT, values).then((res) => res.data),
    {
      onError: () => {},
    }
  );
};

export const useCreateACH = () => {
  return useMutation((values) => axios.post(API.PAYMENT.CREATE_ACH, values), {
    onError: () => {},
    onSuccess: () => {
      queryClient.invalidateQueries('payment-methods');
    },
  });
};

export const useVerifyACH = () => {
  return useMutation((values) => axios.post(API.PAYMENT.VERIFY_ACH, values), {
    onError: () => {},
    onSuccess: () => {
      queryClient.invalidateQueries('payment-methods');
    },
  });
};

export const useChargeACH = () => {
  return useMutation((values) => axios.post(API.PAYMENT.CHARGE_ACH, values));
};

export const useGetTransactions = (filters) => {
  return useQuery(
    ['transactions', filters],
    async () => {
      // Create a copy of filters to avoid mutating the original object
      const cloneFilters = { ...filters };
      const timeFrom =
        cloneFilters?.date?.length > 1
          ? getDateOnly(cloneFilters.date[0])
          : undefined;
      const timeTo =
        cloneFilters?.date?.length > 1
          ? getDateOnly(cloneFilters.date[1])
          : undefined;
      delete cloneFilters.date;
      return axios
        .get(API.PAYMENT.TRANSACTIONS, {
          params: {
            ...cloneFilters,
            timeFrom,
            timeTo,
          },
        })
        .then((res) => res.data);
    },
    {
      keepPreviousData: true,
    }
  );
};

export const useRequestFunds = () => {
  return useMutation((values) => axios.post(API.PAYMENT.REQUEST_FUNDS, values));
};

export const useTrackingAbandonedCarts = () => {
  return useMutation(
    (values) => axios.post(API.PAYMENT.TRACKING_ABANDONED_CARTS, values),
    {
      onError: () => {},
    }
  );
};

export const useSetDefaultPaymentMethod = () => {
  return useMutation(
    (values) => axios.put(API.PAYMENT.DEFAULT_PAYMENT_METHOD, values),
    {
      onSuccess: (_, { onCallback }) => {
        onCallback();
        queryClient.invalidateQueries('payment-methods');
      },
    }
  );
};
